import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

export const HLS_CONVERSION_TIMEOUT_MS = 600000;
export const HLS_STATUSES = ['uploading', 'converting', 'processing', 'ready', 'failed', 'cancelled'];

const DEFAULT_CORE_URL = '/vendor/ffmpeg/0.12.6/ffmpeg-core.js';
const DEFAULT_WASM_URL = '/vendor/ffmpeg/0.12.6/ffmpeg-core.wasm';
const TERMINAL_STATUSES = new Set(['ready', 'failed', 'cancelled']);
const STATUS_MESSAGES = {
    uploading: 'Preparing your video… Please keep this page open.',
    converting: 'Preparing your video… Please keep this page open.',
    processing: 'Finishing your video… Please keep this page open.',
    ready: 'Video is ready.',
    failed: 'The video could not be uploaded. Please try again.',
    cancelled: 'Upload cancelled.',
};

const GUEST_STATUS_COPY = {
    uploading: ['Uploading file', 'Please wait.'],
    converting: ['Preparing your video…', 'Please keep this page open.'],
    processing: ['Preparing your video…', 'Please keep this page open.'],
    ready: ['Video is ready!', 'Share your link above.'],
    failed: ['Upload failed.', 'Please try again.'],
    cancelled: ['Upload cancelled.', 'Choose a file when you are ready.'],
};

const DASHBOARD_STATUS_COPY = {
    uploading: {
        title: 'Uploading media…', badge: 'UPLOADING', message: 'Transferring file directly to Dubz high-speed storage.', progress: 'Transferring packets', link: 'PENDING',
    },
    converting: {
        title: 'Preparing your video…', badge: 'ENCODING', message: 'Please keep this page open while we optimize playback.', progress: 'Optimizing video', link: 'GENERATING',
    },
    processing: {
        title: 'Preparing your video…', badge: 'FINALIZING', message: 'Finishing your video and activating its share link.', progress: 'Finalizing video', link: 'GENERATING',
    },
    ready: {
        title: 'Video ready to share!', badge: 'READY', message: 'Upload and processing completed successfully.', progress: 'Processing finished', link: 'ACTIVE',
    },
    failed: {
        title: 'Upload failed', badge: 'FAILED', message: 'The video could not be uploaded. Please try again.', progress: 'Upload stopped', link: 'UNAVAILABLE',
    },
    cancelled: {
        title: 'Upload cancelled', badge: 'CANCELLED', message: 'Choose the file again whenever you are ready.', progress: 'Upload stopped', link: 'PENDING',
    },
};

function controlFor(form, selector) {
    return form.querySelector(selector)
        || (typeof document !== 'undefined' && document?.querySelector
            ? document.querySelector(`${selector}[data-hls-form="${form.id}"]`)
            : null);
}

function csrfToken(form) {
    return form.querySelector('input[name="_token"]')?.value
        || document.querySelector('meta[name="csrf-token"]')?.content
        || '';
}

function jsonHeaders(form) {
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    const token = csrfToken(form);
    if (token) headers['X-CSRF-TOKEN'] = token;
    return headers;
}

function encodeId(id) {
    return encodeURIComponent(String(id));
}

function uploadUrl(id) {
    return `/hls/uploads/${encodeId(id)}`;
}

function throwResponseError(response, payload) {
    const message = payload?.error || payload?.message || `HLS request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
}

async function jsonResponse(response) {
    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        // Preserve the HTTP status in the useful error below.
    }
    if (!response.ok) throwResponseError(response, payload);
    return payload || {};
}

async function requestJson(url, options, form) {
    const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: { ...jsonHeaders(form), ...(options.headers || {}) },
    });
    return jsonResponse(response);
}

async function uploadMultipart(url, form, fields, fileField, file, signal, onProgress, filename = '') {
    // XHR gives us upload progress while retaining the same-session CSRF
    // cookie as fetch. It also lets cancellation abort an in-flight upload.
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const data = new FormData();
        Object.entries(fields || {}).forEach(([key, value]) => data.append(key, value));
        data.append(fileField, file, filename || file.name || 'upload.bin');
        xhr.open('POST', url, true);
        xhr.withCredentials = true;
        const token = csrfToken(form);
        if (token) xhr.setRequestHeader('X-CSRF-TOKEN', token);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
        };
        xhr.onload = async () => {
            let payload = null;
            try { payload = JSON.parse(xhr.responseText || '{}'); } catch (_) { /* handled below */ }
            if (xhr.status < 200 || xhr.status >= 300) {
                try { throwResponseError({ status: xhr.status }, payload); } catch (error) { reject(error); }
                return;
            }
            resolve(payload || {});
        };
        xhr.onerror = () => reject(new Error('The upload connection failed.'));
        xhr.onabort = () => {
            const error = new DOMException('The upload was cancelled.', 'AbortError');
            reject(error);
        };
        if (signal) {
            if (signal.aborted) { xhr.abort(); return; }
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }
        xhr.send(data);
    });
}

function isAbortError(error) {
    return error?.name === 'AbortError' || error?.code === 20;
}

function timeoutError() {
    const error = new Error('Browser conversion timed out after 600 seconds.');
    error.name = 'HlsConversionTimeoutError';
    return error;
}

function unsupportedError(message = 'Browser HLS conversion is not supported.') {
    const error = new Error(message);
    error.name = 'HlsConversionUnsupportedError';
    return error;
}

/**
 * Convert one source to one HLS VOD rendition using the pinned, single-thread
 * ffmpeg core. The timer begins before the engine is loaded, as the product
 * timeout is elapsed wall clock time rather than media duration.
 */
export async function convertToHls(file, options = {}) {
    const startedAt = Date.now();
    const timings = {};
    const measure = async (name, action) => {
        const start = performance.now();
        try { return await action(); } finally { timings[name] = performance.now() - start; }
    };
    const timeoutMs = options.timeoutMs || HLS_CONVERSION_TIMEOUT_MS;
    const ffmpegFactory = options.ffmpegFactory || (() => new FFmpeg());
    const ffmpeg = ffmpegFactory();
    let timer;
    let logger;
    let terminated = false;
    const terminate = () => {
        if (terminated) return;
        terminated = true;
        if (typeof ffmpeg.terminate === 'function') ffmpeg.terminate();
    };

    const remaining = () => Math.max(1, timeoutMs - (Date.now() - startedAt));
    const raceTimeout = (promise) => new Promise((resolve, reject) => {
        let settled = false;
        let abortListener;
        const finish = (fn, value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (abortListener && options.signal) options.signal.removeEventListener('abort', abortListener);
            fn(value);
        };
        timer = setTimeout(() => {
            terminate();
            finish(reject, timeoutError());
        }, remaining());
        if (options.signal) {
            abortListener = () => {
                terminate();
                const error = new DOMException('The conversion was cancelled.', 'AbortError');
                finish(reject, error);
            };
            if (options.signal.aborted) { abortListener(); return; }
            options.signal.addEventListener('abort', abortListener, { once: true });
        }
        promise.then((value) => finish(resolve, value), (error) => finish(reject, error));
    });

    const run = async (args) => {
        const result = await raceTimeout(ffmpeg.exec(args));
        if (typeof result === 'number' && result !== 0) {
            throw new Error(`Browser conversion failed (ffmpeg exit ${result}).`);
        }
        return result;
    };

    try {
        if (!ffmpeg || typeof ffmpeg.load !== 'function' || typeof ffmpeg.exec !== 'function') {
            throw unsupportedError();
        }

        const coreURL = options.coreURL || DEFAULT_CORE_URL;
        const wasmURL = options.wasmURL || DEFAULT_WASM_URL;
        // The pinned @ffmpeg/core package is the single-thread build. Its
        // worker wrapper is created by @ffmpeg/ffmpeg; there is no separate
        // multi-thread worker asset to fetch.
        const loadOptions = { coreURL, wasmURL };
        if (options.logger && typeof ffmpeg.on === 'function') {
            logger = options.logger;
            ffmpeg.on('log', logger);
        }
        if (typeof options.onEngine === 'function') options.onEngine(ffmpeg);

        // Keep the loading phase inside the same deadline as conversion.
        await measure('engineLoadMs', () => raceTimeout(ffmpeg.load(loadOptions)));
        const inputName = 'input-' + (file.name || 'video').replace(/[^a-z0-9._-]/gi, '_');
        const outputName = 'index.m3u8';
        await measure('inputCopyMs', () => raceTimeout((async () => ffmpeg.writeFile(inputName, await fetchFile(file)))()));

        // A stream-copy probe is enough to distinguish the compatible path,
        // while the output itself is still produced as MPEG-TS HLS below.
        let probeLog = '';
        const probeLogger = (event) => { probeLog += `${event?.message || ''}\n`; };
        if (typeof ffmpeg.on === 'function') ffmpeg.on('log', probeLogger);
        let probeSucceeded = false;
        try {
            await measure('probeMs', () => run(['-hide_banner', '-i', inputName, '-map', '0:v:0', '-map', '0:a:0?', '-c', 'copy', '-f', 'null', '-']));
            // Inspect actual stream descriptions, not the final audio:0kB
            // statistics line emitted even for silent videos.
            const videoStream = probeLog.split('\n').find((line) => /Stream #.*Video:/i.test(line)) || '';
            const audioStream = probeLog.split('\n').find((line) => /Stream #.*Audio:/i.test(line));
            probeSucceeded = /Video:\s*h264\b/i.test(videoStream)
                && (!audioStream || /Audio:\s*aac\b/i.test(audioStream))
                && /yuv420p(?:\(|\s|,)/i.test(videoStream)
                && !/(?:rotate|displaymatrix|rotation)/i.test(probeLog);
        } catch (error) {
            // Encoding below is the normal path for unsupported codecs.
            if (isAbortError(error) || error?.name === 'HlsConversionTimeoutError') throw error;
        } finally {
            if (typeof ffmpeg.off === 'function') ffmpeg.off('log', probeLogger);
        }

        const codecArgs = probeSucceeded
            ? ['-c:v', 'copy', '-c:a', 'copy', '-bsf:v', 'h264_mp4toannexb']
            : ['-vf', 'pad=ceil(iw/2)*2:ceil(ih/2)*2', '-c:v', 'libx264', '-pix_fmt', 'yuv420p', '-preset', 'veryfast', '-crf', '23', '-force_key_frames', 'expr:gte(t,n_forced*6)', '-c:a', 'aac', '-b:a', '128k', '-threads', '1'];
        await measure('conversionMs', () => run([
            '-i', inputName,
            '-map', '0:v:0',
            '-map', '0:a:0?',
            ...codecArgs,
            '-f', 'hls',
            '-hls_time', '6',
            '-hls_playlist_type', 'vod',
            '-hls_flags', 'independent_segments',
            '-hls_segment_filename', 'segment-%05d.ts',
            outputName,
        ]));

        const outputStartedAt = performance.now();

        const files = [];
        if (typeof ffmpeg.listDir !== 'function' || typeof ffmpeg.readFile !== 'function') {
            throw unsupportedError('The bundled ffmpeg engine cannot read its output.');
        }
        const entries = await raceTimeout(ffmpeg.listDir('.'));
        for (const entry of entries) {
            const name = entry.name || entry;
            if (name === outputName || /\.ts$/i.test(name)) {
                const bytes = await raceTimeout(ffmpeg.readFile(name));
                files.push({ name, data: new Blob([bytes], { type: name.endsWith('.m3u8') ? 'application/vnd.apple.mpegurl' : 'video/mp2t' }) });
            }
        }
        const playlist = files.find((entry) => entry.name === outputName);
        if (!playlist || files.filter((entry) => /\.ts$/i.test(entry.name)).length === 0) {
            throw new Error('The browser conversion did not produce a playable HLS VOD.');
        }
        timings.outputReadMs = performance.now() - outputStartedAt;
        return { files, elapsedMs: Date.now() - startedAt, remuxed: probeSucceeded, timings };
    } catch (error) {
        if (error?.name === 'HlsConversionTimeoutError') throw error;
        if (isAbortError(error)) throw error;
        if (error?.name === 'HlsConversionUnsupportedError') throw error;
        throw error;
    } finally {
        clearTimeout(timer);
        if (logger && typeof ffmpeg.off === 'function') ffmpeg.off('log', logger);
        terminate();
    }
}

export class HlsUploadTask {
    constructor(form, options = {}) {
        this.form = form;
        this.options = options;
        this.controller = new AbortController();
        this.id = null;
        this.token = null;
        this.cancelled = false;
        this.started = false;
        this.ffmpeg = null;
        this.pollTimer = null;
        this.timings = {};
        this.startedAt = null;
        this.finishedTiming = false;
    }

    emit(name, detail = {}) {
        this.form.dispatchEvent(new CustomEvent(`hls:${name}`, { bubbles: true, detail }));
        if (typeof this.options.onEvent === 'function') this.options.onEvent(name, detail);
    }

    setStatus(status, detail = {}) {
        if (TERMINAL_STATUSES.has(status) && this.startedAt !== null && !this.finishedTiming) {
            this.finishedTiming = true;
            this.timings.totalMs = performance.now() - this.startedAt;
            if (this.finalizationStartedAt != null) this.timings.finalizationMs = performance.now() - this.finalizationStartedAt;
            // Numeric diagnostics only: no filenames, credentials, or upload tokens.
            this.form.dataset.hlsTimings = JSON.stringify({ ...this.timings, status });
            this.emit('timings', { ...this.timings, status });
        }
        this.form.dataset.hlsStatus = status;
        this.emit('status', { status, ...detail });
    }

    async initialize(file) {
        const kind = this.form.dataset.hlsKind || (this.form.dataset.kind === 'guest' ? 'guest' : 'regular');
        const videoId = this.options.renewVideoId ? undefined : (this.form.dataset.hlsVideoId || this.form.querySelector('[name="video_id"]')?.value || undefined);
        const payload = await requestJson('/hls/uploads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kind, filename: file.name, ...(videoId ? { video_id: videoId } : {}) }),
            signal: this.controller.signal,
        }, this.form);
        this.id = payload.id;
        this.token = payload.token;
        if (payload.public_video_id) {
            this.form.dataset.hlsVideoId = payload.public_video_id;
            const field = this.form.querySelector('[name="video_id"]');
            if (field) field.value = payload.public_video_id;
        }
        if (payload.share_url) {
            const field = this.form.querySelector('[name="video_url"]');
            if (field) field.value = payload.share_url;
            const label = controlFor(this.form, '[data-hls-share]');
            if (label) { label.textContent = payload.share_url; label.setAttribute('link', payload.share_url); }
        }
        this.setStatus(payload.status || 'uploading', payload);
        return payload;
    }

    async uploadOriginal(file) {
        return uploadMultipart(`${uploadUrl(this.id)}/original`, this.form, { token: this.token }, 'video', file, this.controller.signal, (fraction) => {
            this.emit('original-progress', { fraction });
        });
    }

    async uploadFiles(files) {
        let complete = 0;
        const total = files.length;
        // Keep the browser responsive and avoid opening one connection per TS
        // segment while still uploading the rendition quickly.
        const segments = files.filter((file) => /\.ts$/i.test(file.name));
        const playlist = files.filter((file) => /\.m3u8$/i.test(file.name));
        const uploadWithRetries = async (file) => {
            let lastError;
            for (let attempt = 0; attempt < 3; attempt += 1) {
                try {
                    await uploadMultipart(`${uploadUrl(this.id)}/files`, this.form, {
                        token: this.token,
                        filename: file.name,
                    }, 'file', file.data, this.controller.signal, null, file.name);
                    return;
                } catch (error) {
                    lastError = error;
                    if (isAbortError(error) || attempt === 2) throw error;
                    await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
                }
            }
            throw lastError;
        };
        const uploadBatch = async (batch, workers = 3) => {
            const queue = [...batch];
            const worker = async () => {
                while (queue.length) {
                    const file = queue.shift();
                    await uploadWithRetries(file);
                    complete += 1;
                    this.emit('hls-progress', { complete, total, fraction: complete / total });
                }
            };
            await Promise.all(Array.from({ length: Math.min(workers, Math.max(queue.length, 1)) }, worker));
        };
        await uploadBatch(segments);
        // The manifest is published last so a processing worker never sees a
        // playlist whose referenced segments are still being uploaded.
        await uploadBatch(playlist, 1);
    }

    async fallback(error) {
        if (this.cancelled) return;
        this.setStatus('processing');
        await requestJson(`${uploadUrl(this.id)}/fallback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: this.token, error: error?.message || 'Browser conversion failed.' }),
            signal: this.controller.signal,
        }, this.form);
        this.emit('fallback', { error });
    }

    async complete() {
        const payload = await requestJson(`${uploadUrl(this.id)}/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: this.token }),
            signal: this.controller.signal,
        }, this.form);
        this.setStatus(payload.status || 'processing', payload);
        return payload;
    }

    async poll() {
        if (this.cancelled || !this.id) return;
        const payload = await requestJson(`${uploadUrl(this.id)}?token=${encodeURIComponent(this.token)}`, {
            method: 'GET',
            signal: this.controller.signal,
        }, this.form);
        this.setStatus(payload.status, payload);
        if (!TERMINAL_STATUSES.has(payload.status)) {
            this.pollTimer = setTimeout(() => this.poll().catch((error) => this.emit('error', { error })), 1500);
        }
        return payload;
    }

    async start(file) {
        if (this.started || this.cancelled) return;
        this.started = true;
        this.startedAt = performance.now();
        delete this.form.dataset.hlsTimings;
        this.setStatus('uploading');
        try {
            await this.initialize(file);
            this.timings.initializeMs = performance.now() - this.startedAt;
            const originalStartedAt = performance.now();
            let originalError = null;
            // Attach the rejection handler immediately; conversion can take
            // minutes and a failed original upload must not become unhandled.
            const original = this.uploadOriginal(file).finally(() => {
                this.timings.originalUploadMs = performance.now() - originalStartedAt;
            }).catch((error) => {
                originalError = error;
                return null;
            });
            let converted;
            try {
                this.setStatus('converting');
                converted = await convertToHls(file, {
                    ...this.options,
                    signal: this.controller.signal,
                    onEngine: (engine) => { this.ffmpeg = engine; },
                });
            } catch (error) {
                // Fallback is deliberately skipped for user cancellation. It
                // may be sent while the original upload is still in flight.
                if (!this.cancelled && !isAbortError(error)) await this.fallback(error);
                await original;
                if (originalError) throw originalError;
                if (!this.cancelled) await this.poll();
                return { id: this.id, status: 'processing', error };
            }
            Object.assign(this.timings, converted.timings, { remuxed: converted.remuxed, preparationMs: converted.elapsedMs, inputBytes: file.size, outputBytes: converted.files.reduce((sum, file) => sum + file.data.size, 0) });
            const packageStartedAt = performance.now();
            await this.uploadFiles(converted.files);
            this.timings.hlsUploadMs = performance.now() - packageStartedAt;
            // complete is only sent after both the original and all HLS files
            // have arrived, even though original upload started concurrently.
            await original;
            if (originalError) {
                if (!this.cancelled) await this.fallback(originalError);
                throw originalError;
            }
            this.finalizationStartedAt = performance.now();
            const result = await this.complete();
            await this.poll();
            return result;
        } catch (error) {
            if (this.cancelled || isAbortError(error)) return { id: this.id, status: 'cancelled' };
            this.setStatus('failed', { error: error.message || 'Upload failed.' });
            this.emit('error', { error });
            throw error;
        }
    }

    async cancel() {
        if (this.cancelled) return;
        this.cancelled = true;
        clearTimeout(this.pollTimer);
        this.controller.abort();
        if (this.id && this.token) {
            try {
                await requestJson(`${uploadUrl(this.id)}?token=${encodeURIComponent(this.token)}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: this.token }),
                }, this.form);
            } catch (_) {
                // The local cancellation state is still authoritative when a
                // browser has already dropped the network connection.
            }
        }
        this.setStatus('cancelled');
        this.emit('cancelled');
    }
}

export function showFormStatus(form, status, detail = {}) {
    const isGuest = form.dataset.hlsKind === 'guest';
    const feedback = controlFor(form, '[data-hls-feedback]');
    const isDashboard = feedback?.dataset?.variant === 'dashboard';
    if (feedback) {
        feedback.hidden = false;
        feedback.dataset.status = status;
        feedback.setAttribute('aria-busy', TERMINAL_STATUSES.has(status) ? 'false' : 'true');
        feedback.closest?.('.uploadbar-wrapper')?.classList?.add('has-feedback');
    }
    const target = controlFor(form, '[data-hls-status-message]');
    const guestTitle = isGuest ? controlFor(form, '[data-hls-status-title]') : null;
    const dashboardCopy = isDashboard ? DASHBOARD_STATUS_COPY[status] : null;
    if (target) {
        // Only callers that have deliberately prepared user-facing copy may
        // override the generic status. Conversion errors can contain ffmpeg
        // internals, so never render detail.error directly.
        if (dashboardCopy) {
            const title = controlFor(form, '[data-hls-status-title]');
            const badge = controlFor(form, '[data-hls-status-badge]');
            const progressLabel = controlFor(form, '[data-hls-progress-label]');
            const linkBadge = controlFor(form, '[data-hls-link-badge]');
            if (title) title.textContent = dashboardCopy.title;
            if (badge) badge.textContent = dashboardCopy.badge;
            if (progressLabel) progressLabel.textContent = dashboardCopy.progress;
            if (linkBadge) linkBadge.textContent = dashboardCopy.link;
            target.textContent = detail.userMessage || dashboardCopy.message;
        } else if (isGuest && GUEST_STATUS_COPY[status] && !detail.userMessage) {
            const [title, message] = GUEST_STATUS_COPY[status];
            const percent = status === 'uploading' && Number.isFinite(detail.fraction)
                ? ` (${Math.round(detail.fraction * 100)}%)…`
                : status === 'uploading' ? '…' : '';
            if (guestTitle) guestTitle.textContent = `${title}${percent}`;
            target.textContent = message;
        } else {
            if (guestTitle) guestTitle.textContent = status === 'failed' ? 'Upload failed.' : '';
            target.textContent = detail.userMessage || STATUS_MESSAGES[status] || status;
        }
    }
    const progress = controlFor(form, '[data-hls-progress]');
    if (progress) {
        const terminal = TERMINAL_STATUSES.has(status);
        // The original file uploads alongside browser conversion. Its XHR can
        // reach 100% while conversion is still active, so active work stays
        // indeterminate instead of suggesting the whole operation is done.
        progress.removeAttribute('value');
        progress.style.display = terminal && !((isGuest || isDashboard) && status === 'ready') ? 'none' : '';
        const bar = isGuest || isDashboard ? controlFor(form, '[data-hls-progress-bar]') : null;
        if (bar) {
            if (status === 'uploading') {
                const fraction = Number.isFinite(detail.fraction) ? Math.max(0, Math.min(1, detail.fraction)) : 0;
                bar.style.width = `${Math.round(fraction * 100)}%`;
                progress.setAttribute?.('aria-valuenow', String(Math.round(fraction * 100)));
                const progressValue = isDashboard ? controlFor(form, '[data-hls-progress-value]') : null;
                if (progressValue) progressValue.textContent = `${Math.round(fraction * 100)}%`;
            } else if (status === 'ready') {
                bar.style.width = '100%';
                progress.setAttribute?.('aria-valuenow', '100');
                const progressValue = isDashboard ? controlFor(form, '[data-hls-progress-value]') : null;
                if (progressValue) progressValue.textContent = '100%';
            } else {
                bar.style.width = '';
                progress.removeAttribute('aria-valuenow');
                const progressValue = isDashboard ? controlFor(form, '[data-hls-progress-value]') : null;
                if (progressValue) progressValue.textContent = 'In progress';
            }
        }
    }
    const cancel = controlFor(form, '[data-hls-cancel]');
    if (cancel) cancel.style.display = TERMINAL_STATUSES.has(status) ? 'none' : '';
}

export function mountHlsUploads(root = document) {
    const tasks = [];
    root.querySelectorAll('form[data-hls-upload]').forEach((form) => {
        if (form.__hlsUploadTask) return;
        const input = form.querySelector('input[type="file"]');
        const dropzone = form.querySelector('[data-hls-dropzone]') || document.querySelector(`[data-hls-dropzone-for="${form.id}"]`);
        if (!input) return;
        let task = new HlsUploadTask(form);
        form.__hlsUploadTask = task;
        tasks.push(task);

        const choose = (files) => {
            const file = files && files[0];
            if (!file) return;
            const maxBytes = form.dataset.hlsKind === 'guest' ? 100 * 1024 * 1024 : 300 * 1024 * 1024;
            if (file.size > maxBytes) {
                showFormStatus(form, 'failed', { error: `File size must be less than ${form.dataset.hlsKind === 'guest' ? 100 : 300} MB.` });
                input.value = '';
                return;
            }
            if (task.started && !TERMINAL_STATUSES.has(form.dataset.hlsStatus)) return;
            if (task.started || task.cancelled) {
                task = new HlsUploadTask(form, { renewVideoId: true });
                form.__hlsUploadTask = task;
            }
            task.start(file).catch((error) => task.emit('error', { error }));
        };
        input.addEventListener('change', () => choose(input.files));
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            choose(input.files);
        });
        if (dropzone) {
            dropzone.addEventListener('click', (event) => {
                if (event.target !== input && !event.target.closest('label,button,a')) input.click();
            });
            dropzone.addEventListener('dragover', (event) => { event.preventDefault(); dropzone.classList.add('dragover'); });
            dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
            dropzone.addEventListener('drop', (event) => {
                event.preventDefault();
                dropzone.classList.remove('dragover');
                choose(event.dataTransfer.files);
            });
        }
        form.addEventListener('hls:status', (event) => {
            const feedback = controlFor(form, '[data-hls-feedback]');
            const phased = form.dataset.hlsKind === 'guest' || feedback?.dataset?.variant === 'dashboard';
            if (phased && event.detail.status === 'converting' && form.dataset.hlsOriginalComplete !== 'true') {
                showFormStatus(form, 'uploading', { fraction: Number(form.dataset.hlsOriginalProgress || 0) });
                return;
            }
            showFormStatus(form, event.detail.status, event.detail);
        });
        form.addEventListener('hls:original-progress', (event) => {
            if (!TERMINAL_STATUSES.has(form.dataset.hlsStatus)) {
                const feedback = controlFor(form, '[data-hls-feedback]');
                const phased = form.dataset.hlsKind === 'guest' || feedback?.dataset?.variant === 'dashboard';
                if (phased) {
                    const fraction = Number(event.detail?.fraction || 0);
                    form.dataset.hlsOriginalProgress = String(fraction);
                    if (fraction >= 1) {
                        form.dataset.hlsOriginalComplete = 'true';
                        showFormStatus(form, form.dataset.hlsStatus === 'uploading' ? 'uploading' : 'converting', { fraction });
                    } else {
                        showFormStatus(form, 'uploading', { fraction });
                    }
                    return;
                }
                // Keep the current conversion/server message; original upload
                // progress does not represent completion of the task.
                showFormStatus(form, form.dataset.hlsStatus || 'uploading');
            }
        });
        form.addEventListener('hls:hls-progress', (event) => {
            const feedback = controlFor(form, '[data-hls-feedback]');
            if (form.dataset.hlsKind === 'guest' || feedback?.dataset?.variant === 'dashboard') form.dataset.hlsOriginalComplete = 'true';
            if (!TERMINAL_STATUSES.has(form.dataset.hlsStatus)) showFormStatus(form, 'converting', event.detail);
        });
        form.addEventListener('hls:error', (event) => showFormStatus(form, 'failed', event.detail));
        controlFor(form, '[data-hls-cancel]')?.addEventListener('click', () => task.cancel());
    });
    return tasks;
}

if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => mountHlsUploads());
}
